import React, { useState } from 'react';
import axios from 'axios';
import DataList from '../DataList';
import { getFormattedISODateTime } from '../../assets/Utils';
import { useLocation } from 'react-router-dom';
import ApiConfigFactory from '../../configurations/ApiConfigFactory';
import { TAKE_HOME_STATUS_LABELS, STATUS_LABELS } from '../Admissions/DischargeModal';

const PatientAdmissionList = ({ patient: patientProp }) => {
  const location = useLocation();
  const patient = patientProp || location.state?.patient;
  const id = patient ? patient.patient_id : undefined;

  // State for discharge consent warning modal
  const [consentWarning, setConsentWarning] = useState({ show: false, message: '', onConfirm: null });
  const [adminDischargeWarning, setAdminDischargeWarning] = useState({
    show: false, title: '', message: '', confirmLabel: 'Discharge Anyway', onConfirm: null, onCancel: null
  });
  
  // Set a title based on the patient information.
  const title = patient
    ? `Admissions for Patient: ${patient.patient_first_name} ${patient.patient_last_name} - ${patient.patient_number}`
    : 'All Admissions';

  // Function to validate sales number and patient ID match
  const validateSalesNumberAndPatientId = async (formData) => {
    const salesNumber = formData.sales_number;
    const formPatientId = formData.patient_id;
    
    // Allow empty sales numbers - no validation required
    if (!salesNumber || salesNumber.trim() === '') {
      return { isValid: true };
    }

    try {
      console.log('Validating sales number:', salesNumber);
      const response = await axios.get(`${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/billing/sales-record/${salesNumber}`);
      const data = response.data;
      
      console.log('Sales record data:', data);

      // If we get a response but no data, the sales number is invalid
      if (!data) {
        return {
          isValid: false,
          message: `Sales number "${salesNumber}" not found. Please verify the sales number is correct.`
        };
      }
      
      // If sales record has a patient ID and form has a patient ID, they must match
      if (data.patient_id && formPatientId) {
        if (data.patient.patient_id !== formPatientId) {
          return {
            isValid: false,
            message: `Patient ID mismatch: Sales record patient ID (${data.patient.patient_id}) does not match the admission patient ID (${formPatientId}). Please verify the correct sales number.`
          };
        }
      }
      
      return { isValid: true };
    } catch (error) {
      console.error('Error validating sales record:', error);
      return {
        isValid: false,
        message: `Sales number "${salesNumber}" is invalid or could not be verified. Please check the sales number and try again.`
      };
    }
  };

  // Initialize apiConfig using ApiConfigFactory
  const apiConfig = new ApiConfigFactory('patients/admissions', title, 'patient_id', id);
  apiConfig.allowAdd = id ? true : false;
  apiConfig.path = '/patient-admissions';
  apiConfig.defaultSortAsc = false;
  apiConfig.defaultSortFieldId = 'admission_id';
  
  const promptAdminWarning = ({ title, message, confirmLabel }) => new Promise((resolve) => {
    setAdminDischargeWarning({
      show: true,
      title,
      message,
      confirmLabel: confirmLabel || 'Discharge Anyway',
      onConfirm: () => {
        setAdminDischargeWarning({ show: false, title: '', message: '', confirmLabel: 'Discharge Anyway', onConfirm: null, onCancel: null });
        resolve({ isValid: true });
      },
      onCancel: () => {
        setAdminDischargeWarning({ show: false, title: '', message: '', confirmLabel: 'Discharge Anyway', onConfirm: null, onCancel: null });
        resolve({ isValid: false, message: 'Discharge cancelled.' });
      }
    });
  });

  // Combined validation: sales number + doctor discharge order + take-home + consent check on discharge
  apiConfig.additionalValidation = async (formData) => {
    const salesResult = await validateSalesNumberAndPatientId(formData);
    if (!salesResult.isValid) return salesResult;

    if (formData.discharge_date && formData.clinical_status !== 'discharged') {
      let clinicalStatus = formData.clinical_status;
      let takeHomeStatus = formData.take_home_status;
      if (!clinicalStatus && formData.admission_id) {
        try {
          const response = await axios.get(
            `${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/patients/admissions/${formData.admission_id}`
          );
          clinicalStatus = response.data.clinical_status;
          takeHomeStatus = response.data.take_home_status;
        } catch (error) {
          console.error('Failed to load discharge status:', error);
        }
      }

      if (clinicalStatus !== 'ready_for_discharge' && clinicalStatus !== 'discharged') {
        const orderResult = await promptAdminWarning({
          title: 'No doctor discharge order',
          message: 'The doctor has not signed a discharge order (final assessment and take-home medicines). The patient should not normally be discharged until the doctor does this.',
          confirmLabel: 'Discharge Anyway'
        });
        if (!orderResult.isValid) return orderResult;
      }

      if (takeHomeStatus === 'pending') {
        const pharmacyResult = await promptAdminWarning({
          title: 'Take-home medicines not dispensed',
          message: 'Pharmacy has not yet dispensed the take-home medications. You can still complete discharge if the patient will collect medicines later or buy them outside.',
          confirmLabel: 'Discharge Anyway'
        });
        if (!pharmacyResult.isValid) return pharmacyResult;
      }
    }

    // Check consent on discharge
    if (formData.discharge_date) {
      const missingConsent = [];
      if (!formData.consentFile) missingConsent.push('General Consent Form');
      const isICU = formData.ward && formData.ward.toUpperCase().includes('ICU');
      if (isICU && !formData.icuConsentFile) missingConsent.push('ICU Consent Form');

      if (missingConsent.length > 0) {
        // Return a special flag so we can show the warning modal
        return new Promise((resolve) => {
          setConsentWarning({
            show: true,
            message: `This patient is being discharged without the following consent form(s): ${missingConsent.join(', ')}. An email notification will be sent to Admin and Medical Director.`,
            onConfirm: () => {
              // Fire-and-forget email notification
              axios.post(
                `${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/admissions/notify-missing-consent`,
                {
                  admissionId: formData.admission_id,
                  patientName: formData.patient_first_name
                    ? `${formData.patient_first_name} ${formData.patient_last_name}`
                    : `Patient #${formData.patient_id}`,
                  missingForms: missingConsent,
                  ward: formData.ward
                }
              ).catch(err => console.error('Failed to send consent notification:', err));
              setConsentWarning({ show: false, message: '', onConfirm: null });
              resolve({ isValid: true });
            },
            onCancel: () => {
              setConsentWarning({ show: false, message: '', onConfirm: null });
              resolve({ isValid: false, message: 'Discharge cancelled — please upload consent forms first.' });
            }
          });
        });
      }
    }

    return { isValid: true };
  };
  
  // Configure fields, columns, and other properties
  apiConfig
    .addField({
      name: 'admission_id',
      label: 'Admission ID',
      type: 'number',
      identifier: true,
      disabled: true,
      tab: 'Admission Details'
    })
    .addField({
      name: 'patient_id',
      label: 'Patient ID',
      type: 'number',
      tab: 'Admission Details',
      defaultValue: id,
      visible: false
    })
    .addField({
      name: 'admission_date',
      label: 'Admission Date',
      type: 'datetime-local',
      required: true,
      tab: 'Admission Details',
      defaultValue: getFormattedISODateTime()
    })
    .addField({
      name: 'branch',
      label: 'Branch',
      type: 'dropdown',
      required: true,
      tab: 'Admission Details'
    })
    .addField({
      name: 'ward',
      label: 'Ward',
      type: 'dropdown',
      required: true,
      tab: 'Admission Details'
    })
    .addField({
      name: 'bed',
      label: 'Bed',
      type: 'text',
      required: false,
      tab: 'Admission Details'
    })
    .addField({
      name: 'discharge_date',
      label: 'Discharge Date',
      type: 'datetime-local',
      required: false,
      tab: 'Transfer/Discharge'
    })
    .addField({
      name: 'clinical_status_label',
      label: 'Doctor Discharge Order',
      type: 'text',
      required: false,
      tab: 'Transfer/Discharge',
      disabled: true,
      onInitialize: (value, formData) => STATUS_LABELS[formData.clinical_status] || STATUS_LABELS.admitted
    })
    .addField({
      name: 'take_home_status_label',
      label: 'Take-home Medicines',
      type: 'text',
      required: false,
      tab: 'Transfer/Discharge',
      disabled: true,
      onInitialize: (value, formData) => TAKE_HOME_STATUS_LABELS[formData.take_home_status] || TAKE_HOME_STATUS_LABELS.none
    })
    .addField({
      name: 'transfer_date',
      label: 'Transfer Date',
      type: 'datetime-local',
      required: false,
      tab: 'Transfer/Discharge'
    })
    .addField({
      name: 'transfer_location',
      label: 'Transfer Location',
      type: 'text',
      required: false,
      tab: 'Transfer/Discharge'
    })
    .addField({
      name: 'admissionReason',
      label: 'Admission Reason',
      type: 'textarea',
      required: false,
      tab: 'Admission Details',
      maxRows: 5
    })
    .addField({
      name: 'consentFile',
      label: 'General Consent Form',
      type: 'file',
      required: false,
      tab: 'Files'
    })
    .addField({
      name: 'icuConsentFile',
      label: 'ICU Consent Form',
      type: 'file',
      required: false,
      tab: 'Files'
    })
    .addField({
      name: 'admissionFiles',
      label: 'Other Documents',
      type: 'file',
      required: false,
      tab: 'Files'
    })
    //add a field for the sales number
    .addField({
      name: 'sales_number',
      label: 'Sales Number',
      type: 'text',
      required: false,
      tab: 'Billing'
    })
    //add a field to hold the retrieved patient name based on the sales number
    .addField({
      name: 'patient_name',
      label: 'Patient Name',
      type: 'text',
      required: false,
      tab: 'Billing',
      disabled: true,
     
    })
    //add a field for patient id
    .addField({
      name: 'patient_id',
      label: 'Patient ID',
      type: 'text',
      required: false,
      tab: 'Billing',
      disabled: true
    })

    .addStaticDropdownField({
      name: 'branch',
      options: [
        { id: 'Poultney', name: 'Poultney' },
        { id: 'Kissy', name: 'Kissy' }
      ]
    })
    .addStaticDropdownField({
      name: 'ward',
      options: [
        { id: 'Children', name: 'Children' },
        { id: 'Adult - Male', name: 'Adult - Male' },
        { id: 'Adult - Female', name: 'Adult - Female' },
        { id: 'Maternity', name: 'Maternity' },
        { id: 'ICU', name: 'ICU' },
        { id: 'Private - Male', name: 'Private - Male' },
        { id: 'Private - Female', name: 'Private - Female' }
      ]
    })
    .addTab({
      name: 'Admission Details',
      roles: 'all',
      default: true
    })
    .addTab({
      name: 'Transfer/Discharge',
      roles: 'all'
    })
    .addTab({
      name: 'Files',
      roles: 'all'
    })
    .addTab({
      name: 'Billing',
      roles: 'Admin, Cashier,Accountant,Manager,Pharmacist'
    })
    .addBulkAction({
      name: 'Generate Report',
      endpoint: '/hospital-portal/api/patient-admissions/generate-report'
    })
    .addColumn({
      id: 'admission_id',
      name: 'ID',
      selector: (row) => row.admission_id,
      type: 'number',
      sortable: true,
      identifier: true,
      width: '100px',
      filterable: true,
      filterSelector: 'admission_id',
      width: '50px'
    })
    .addColumn({
      name: 'Detail',
      type: 'link',
      label: 'Detail',
      path: '/admission-card',
      getState: (row) => ({ admission: row }),
      width: '70px',
    })
    .addColumn({
      name: 'Patient Number',
      selector: (row) => row.Patient.patient_number,
      type: 'number',
      sortable: true,
      filterable: true,
      filterSelector: 'Patient.patient_number',
      width: '100px'
    })
    .addColumn({
      name: 'First Name',
      selector: (row) => row.Patient.patient_first_name,
      type: 'text',
      sortable: true,
      filterable: true,
      filterSelector: 'Patient.patient_first_name'
    })
    .addColumn({
      name: 'Last Name',
      selector: (row) => row.Patient.patient_last_name,
      type: 'text',
      sortable: true,
      filterable: true,
      filterSelector: 'Patient.patient_last_name'
    })
    .addColumn({
      name: 'Admission Date',
      selector: (row) => row.admission_date,
      type: 'date',
      sortable: true,
      width: '100px',
      filterable: true,
      filterSelector: 'admission_date',
    })
    .addColumn({
      name: 'Status',
      selector: (row) => STATUS_LABELS[row.clinical_status] || (row.discharge_date ? 'Discharged' : 'Admitted'),
      cell: (row) => {
        if (row.clinical_status === 'ready_for_discharge') {
          return <span style={{ background: '#e8f5e9', color: '#1b5e20', fontWeight: 700, padding: '2px 8px', borderRadius: '12px', fontSize: '0.75rem' }}>Ready for Discharge</span>;
        }
        if (row.discharge_date || row.clinical_status === 'discharged') {
          return 'Discharged';
        }
        return 'Admitted';
      },
      sortable: false,
      width: '150px'
    })
    .addColumn({
      name: 'Discharge Date',
      selector: (row) => row.discharge_date,
      type: 'date',
      sortable: true,
      width: '100px',
      filterable: true,
      filterSelector: 'discharge_date',
    })
    .addColumn({
      name: 'Branch',
      selector: (row) => row.branch,
      sortable: true,
      filterable: true,
      filterSelector: 'branch',
      width: '100px'
    })
    .addColumn({
      name: 'Ward',
      selector: (row) => row.ward,
      sortable: true,
      filterable: true,
      filterSelector: 'ward',
      width: '100px'
    })
    .addColumn({
      name: 'Consent',
      selector: (row) => {
        const hasConsent = !!row.consentFile;
        const isICU = row.ward && row.ward.toUpperCase().includes('ICU');
        const hasICU = !!row.icuConsentFile;
        if (isICU) {
          if (hasConsent && hasICU) return '✓';
          const parts = [];
          if (!hasConsent) parts.push('General');
          if (!hasICU) parts.push('ICU');
          return `✗ ${parts.join(', ')}`;
        }
        return hasConsent ? '✓' : '✗ Missing';
      },
      cell: (row) => {
        const hasConsent = !!row.consentFile;
        const isICU = row.ward && row.ward.toUpperCase().includes('ICU');
        const hasICU = !!row.icuConsentFile;
        const complete = hasConsent && (isICU ? hasICU : true);
        if (complete) {
          return <span style={{ color: '#28a745', fontWeight: 'bold' }}>✓</span>;
        }
        const parts = [];
        if (!hasConsent) parts.push('General');
        if (isICU && !hasICU) parts.push('ICU');
        return (
          <span style={{ color: '#dc3545', fontWeight: 'bold' }} title={`Missing: ${parts.join(', ')}`}>
            ⚠ Missing
          </span>
        );
      },
      sortable: false,
      width: '100px'
    });

  // Conditional row styles: highlight rows with missing consent
  apiConfig.conditionalRowStyles = [
    {
      when: (row) => row.clinical_status === 'ready_for_discharge' && !row.discharge_date,
      style: {
        backgroundColor: '#e8f5e9',
        borderLeft: '3px solid #2e7d32',
      },
    },
    {
      when: (row) => {
        const hasConsent = !!row.consentFile;
        const isICU = row.ward && row.ward.toUpperCase().includes('ICU');
        const hasICU = !!row.icuConsentFile;
        return !(hasConsent && (isICU ? hasICU : true));
      },
      style: {
        backgroundColor: '#fff8e1',
        borderLeft: '3px solid #ff9800',
      },
    },
  ];

  return (
    <>
      <DataList apiConfig={apiConfig} columns={apiConfig.columns} />

      {/* Discharge Consent Warning Modal */}
      {consentWarning.show && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', zIndex: 9999
        }}>
          <div style={{
            background: 'white', borderRadius: '8px', padding: '24px',
            maxWidth: '500px', width: '90%', boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
              <span style={{ fontSize: '2rem' }}>⚠️</span>
              <h3 style={{ margin: 0, color: '#e65100' }}>Missing Consent Forms</h3>
            </div>
            <p style={{ color: '#333', lineHeight: '1.5' }}>{consentWarning.message}</p>
            <p style={{ color: '#666', fontSize: '0.9rem', fontStyle: 'italic' }}>
              Do you want to proceed with the discharge?
            </p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}>
              <button
                onClick={consentWarning.onCancel}
                style={{
                  padding: '8px 20px', border: '1px solid #ccc',
                  borderRadius: '4px', background: 'white', cursor: 'pointer'
                }}
              >
                Cancel — Upload First
              </button>
              <button
                onClick={consentWarning.onConfirm}
                style={{
                  padding: '8px 20px', border: 'none', borderRadius: '4px',
                  background: '#e65100', color: 'white', cursor: 'pointer', fontWeight: 'bold'
                }}
              >
                Discharge Anyway
              </button>
            </div>
          </div>
        </div>
      )}
      {adminDischargeWarning.show && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', zIndex: 9999
        }}>
          <div style={{
            background: 'white', borderRadius: '8px', padding: '24px',
            maxWidth: '500px', width: '90%', boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
              <span style={{ fontSize: '2rem' }}>⚠️</span>
              <h3 style={{ margin: 0, color: '#e65100' }}>{adminDischargeWarning.title}</h3>
            </div>
            <p style={{ color: '#333', lineHeight: '1.5' }}>{adminDischargeWarning.message}</p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}>
              <button
                onClick={adminDischargeWarning.onCancel}
                style={{
                  padding: '8px 20px', border: '1px solid #ccc',
                  borderRadius: '4px', background: 'white', cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={adminDischargeWarning.onConfirm}
                style={{
                  padding: '8px 20px', border: 'none', borderRadius: '4px',
                  background: '#e65100', color: 'white', cursor: 'pointer', fontWeight: 'bold'
                }}
              >
                {adminDischargeWarning.confirmLabel}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PatientAdmissionList;
